// ป๊อปอัปครั้งแรกของวัน — แสดงการ์ดชุดเดียวกับแถบหน้าแรก มีแค่ปุ่ม "ปิด" (ไม่มี "ไม่ต้องแสดงอีก" และปิดถาวรไม่ได้)
// server บันทึกว่าแสดงแล้ววันนี้ จึงไม่เด้งซ้ำเมื่อเปลี่ยนหน้าหรือเปลี่ยนเครื่อง
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import api from '../../adapters/api';
import InsightCard from './InsightCard';
import InsightRowsDrawer, { rowActionFor } from './InsightRowsDrawer';
import useInsightTasks, { logInsightEvent } from './useInsightTasks';
import { useInsightMe } from './useInsightInline';

export default function DailyInsightDialog() {
  const [open, setOpen] = useState(false);
  const [rowsTask, setRowsTask] = useState(null);
  const shownRef = useRef(false); // เปิดได้ครั้งเดียวต่อการโหลดหน้า (server กันซ้ำข้ามหน้า/เครื่อง)
  const { data: me } = useInsightMe();
  const enabled = Boolean(me?.channels?.popup);
  const { data: popup } = useQuery({
    queryKey: ['insightPopup'],
    queryFn: async () => (await api.get('/api/insight/popup')).data,
    enabled,
    staleTime: Infinity,
    retry: false,
  });
  const { data: daily, actionsFor, readOnly } = useInsightTasks(enabled && Boolean(popup?.show));
  const tasks = useMemo(() => daily?.tasks || [], [daily]);

  // เปิดเมื่อ server บอกว่าวันนี้ยังไม่ได้แสดง และโหลดการ์ดแล้ว — บันทึกทันทีที่แสดง
  useEffect(() => {
    if (!popup?.show || !tasks.length || shownRef.current) return;
    shownRef.current = true;
    setOpen(true);
    api.post('/api/insight/popup/shown').catch(() => {});
    tasks.forEach(t => logInsightEvent(t.id, 'seen_popup'));
  }, [popup, tasks]);

  if (!enabled) return null;

  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <LightbulbOutlinedIcon color="warning" />
          วันนี้มี {tasks.length} เรื่องที่ควรดู
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            ดูซ้ำได้ตลอดวันที่การ์ดข้างช่องค้นหาสินค้า หรือไอคอน 💡 มุมขวาบน
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {tasks.map(t => (
              <InsightCard key={t.id} task={t} minWidth={260} actions={actionsFor(t, { onOpenRows: setRowsTask })} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>
      <InsightRowsDrawer
        key={rowsTask?.id || 'none'}
        open={Boolean(rowsTask)} onClose={() => setRowsTask(null)}
        url={rowsTask ? `/api/insight/tasks/${rowsTask.id}/rows` : null}
        title={rowsTask?.title}
        onDownload={rowsTask ? () => actionsFor(rowsTask).download() : undefined}
        onCopy={rowsTask ? () => logInsightEvent(rowsTask.id, 'copy') : undefined}
        rowAction={rowActionFor(rowsTask, readOnly)}
      />
    </>
  );
}
